import React from 'react';
import {Table, TableBody, TableCell, TableHead, TableRow, TextField, Theme, WithStyles } from '@material-ui/core';
import {withStyles} from '@material-ui/core/styles';
import BayesianNetworkMxGraph, {BayesianNodeStateJSON} from '../../../../../mxGraph/HCLTreeMxGraph/BayesianNetworkMxGraph';
import BayesianNetworkVertex from '../../../../../mxGraph/HCLTreeNode/BayesianNetworkNode/BayesianNetworkVertex';
import BayesianNetworkVertexValue
  from "../../../../../mxGraph/HCLTreeNode/BayesianNetworkNode/BayesianNetworkVertexValue";
import VertexSelectionDialog from "./VertexSelectionDialog";
import LabelInput from "../Inputs/LabelInput";
import {Label, LabelJSON} from "../../../../../mxGraph/HCLTreeNode/HCLTreeVertexValue/Label";
import AddIcon from "@material-ui/icons/AddBox";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import DeleteIcon from "@material-ui/icons/DeleteForever";
import EditIcon from "@material-ui/icons/Edit";
import CancelIcon from "@material-ui/icons/Close";
import {extractStates, generateTableData, transpose} from "../Tools/Utils";
import { fade } from '@material-ui/core/styles/colorManipulator';
import InputRules from "../Inputs/InputRules";
import Expression from "../../../../../mxGraph/HCLTreeNode/HCLTreeVertexValue/Expression";
import ProxyTypes from "../../../../../mxGraph/HCLTreeNode/HCLTreeVertexValue/ProxyTypes";
import Outcome from "../../../../../mxGraph/HCLTreeNode/HCLTreeVertexValue/Outcome";
import ReferenceTypes from "../../../../../mxGraph/HCLTreeNode/HCLTreeVertexValue/ReferenceTypes";


const styles = (theme: Theme) => ({
  tableDataCell: {
    minWidth: "10rem",
    padding: `0px ${theme.spacing(3)}px`,
  }
});

interface Props extends WithStyles<typeof styles> {
  graph: BayesianNetworkMxGraph;
  targetCell: BayesianNetworkVertex;
  onClose: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  onSave: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
}

interface State {
  label: LabelJSON;
  labelError: boolean;
  transposedMatrix: (string | number)[][];
  numRows: number;
  numCols: number;
  parents: BayesianNetworkVertex[];
  targetStateNames: string[];
  editing: boolean;
  probabilityErrors: boolean[];
  targetStateErrors: boolean;
}

const StyledTableCell = withStyles(theme => ({
  root: {
    '&:nth-child(odd)': {
      backgroundColor: fade(theme.palette.text.primary, 0.12),
    },
  },
}))(TableCell);

const StyledTableRow = withStyles(theme => ({
}))(TableRow);

class BayesianNetworkMxGraphDialog extends React.Component<Props, State> {

  private tableRef: React.RefObject<HTMLDivElement>;

  constructor(props: Props) {
    super(props);

    const { graph, targetCell } = this.props;
    const targetCellData = targetCell.getValue() as BayesianNetworkVertexValue;

    const [numRows, numCols, parents, targetStateNames, matrix] = generateTableData(graph, targetCell);
    const transposedMatrix = transpose(matrix);

    this.tableRef = React.createRef();

    this.state = {
      label: targetCellData.getLabel().toJSON(),
      labelError: false,
      transposedMatrix: transposedMatrix,
      numRows: numRows,
      numCols: numCols,
      parents: parents,
      targetStateNames: targetStateNames,
      editing: false,
      probabilityErrors: new Array(numRows).fill(false),
      targetStateErrors: false,
    };
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
    this.tableRef.current.scrollLeft = this.tableRef.current.scrollWidth;
  }

  /**
   * handle input change of text fields
   * @param {Object} e - event object
   * @param {string} key - This tells us which text field the event is coming from.
   * @param {boolean} multiple - A flag should be set if the target contains multiple changes
   *  Specified by name field of {@link LabelInput} and {@link ExpressionInput}.
   */
  private handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
    key: string,
    multiple?: boolean
  ) => {

    e.persist();

    // change the entire object
    if (multiple) {
      // @ts-ignore
      this.setState(prevState => ({ [key]: { ...prevState[key], ...e.target.value } }));
    }
    // change a field
    else {
      // @ts-ignore
      this.setState(state => ({
        [key]: {
          // @ts-ignore
          ...state[key],
          [e.target.name]: e.target.value
        },
      }));
    }
  };

  /**
   * Changes state on probability change
   * @param {React.ChangeEvent<HTMLInputElement>} e
   * @param {number} rowIndex
   * @param {number} colIndex
   */
  private handleProbabilities = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>, rowIndex: number, colIndex: number) => {
    const { parents } = this.state;
    e.persist();
    this.setState(prevState => {
      const matrix = [...prevState.transposedMatrix];
      matrix[rowIndex][colIndex] = Number(e.target.value);
      return {
        transposedMatrix: matrix,
        probabilityErrors: [
          ...prevState.probabilityErrors.slice(0, rowIndex),
          !InputRules.isEqualToOne(matrix[rowIndex].slice(parents.length) as number[]),
          ...prevState.probabilityErrors.slice(rowIndex + 1)
        ]
      }
    });
  };

  private handleLabelError = (error: boolean) => {
    this.setState({ labelError: error });
  };

  private handleAddState = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    this.setState(prevState => ({
      numCols: prevState.numCols + 1,
      targetStateNames: [...prevState.targetStateNames, "new state"],
      transposedMatrix: prevState.transposedMatrix.map(row => {
        row.push(0);
        return row;
      })
    }), () => this.checkDuplicates(this.state.targetStateNames));
  };

  private handleDeleteState = (e: React.MouseEvent<HTMLElement, MouseEvent>, index: number) => {

    this.setState(prevState => {

      const { numCols, targetStateNames, parents } = prevState;
      const numDependentCols = numCols - targetStateNames.length;

      const matrix = prevState.transposedMatrix.map(row => [...row.slice(0, numDependentCols + index), ...row.slice(numDependentCols + index + 1)]);

      return ({
        numCols: prevState.numCols - 1,
        targetStateNames: [...prevState.targetStateNames.slice(0, index), ...prevState.targetStateNames.slice(index + 1)],
        transposedMatrix: matrix,
        probabilityErrors: matrix.map(row => !InputRules.isEqualToOne(row.slice(parents.length) as number[]))
      });
    });

  };

  private checkDuplicates  = (stateName: string[]) => {
    this.setState({
      targetStateErrors: !InputRules.checkIfArrayIsUnique(stateName) || stateName.some(item => InputRules.isEmpty(item))
    });
  };

  private handleSave = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    const { onSave, targetCell, graph } = this.props;
    const { transposedMatrix, targetStateNames, numRows, parents, label } = this.state;

    const value = targetCell.getValue() as BayesianNetworkVertexValue;

    const states: {[key: string]: BayesianNodeStateJSON} = {};
    targetStateNames.forEach((stateName, stateCol) => {
      states[stateName] = {
        probabilities: new Array(numRows).fill(0).map((_, row) => ({
          expression: new Expression({
            value: Number(transposedMatrix[row][parents.length + stateCol]),
            _proxy: ProxyTypes.FLOAT
          }).toJSON(),

          states: new Array(parents.length).fill(0).map((_, dependentCol) =>
            new Outcome(
              transposedMatrix[row][dependentCol] as string,
              ReferenceTypes.STATES, graph.getModelTreeId(),
              `bayesian_nodes.${(parents[dependentCol]).getKey()}`
            ).toJSON()
          )
        }))
      }
    });
    value.setStates(states);
    value.setLabel(Label.build(label));
    onSave(e);
  };

  private truthy = (element: boolean) => {
    return element === true;
  };

  private toggleEditState = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    this.setState(prevState => ({ editing: !prevState.editing }));
  };

  private handleTargetStateNameChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>, index: number) => {
    e.persist();
    this.setState(prevState => ({
      targetStateNames: [
        ...prevState.targetStateNames.slice(0, index),
        e.target.value,
        ...prevState.targetStateNames.slice(index + 1)
      ]
    }), () => this.checkDuplicates(this.state.targetStateNames))
  };

  render() {
    const { targetCell, onClose, classes } = this.props;
    const { label, labelError, transposedMatrix, numCols, parents, targetStateNames, probabilityErrors, editing, targetStateErrors } = this.state;

    const numDependentCols = numCols - targetStateNames.length;
    const numTargetStates = targetStateNames.length;

    const stateNameError = !InputRules.checkIfArrayIsUnique(this.state.targetStateNames) || this.state.targetStateNames.some(item => InputRules.isEmpty(item));
    return (
      <VertexSelectionDialog
        vertex={targetCell}
        onClose={onClose}
        onSave={this.handleSave}
        error={labelError || probabilityErrors.some(this.truthy) || targetStateErrors}
        open
        maxWidth="lg"
      >
        <LabelInput
          label={Label.build(label)}
          onChange={this.handleChange}
          name="label"
          onError={this.handleLabelError}
          gridItemProps={{
            xs: 12,
            sm: 4,
          }}
        />
        <div style={{ width: "100%", display: "flex" }}>
          <div style={{ flexGrow: 1 }} />
          <Tooltip  title={editing ? "Cancel" : "Edit"}>
            <IconButton onClick={this.toggleEditState}>
              {editing ? <CancelIcon /> : <EditIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Add State">
            <IconButton onClick={this.handleAddState}>
              <AddIcon />
            </IconButton>
          </Tooltip>
        </div>
        <div style={{ overflowX: "auto" }} ref={this.tableRef}>
          <Table>
            <TableHead>
              {
                editing &&
                <StyledTableRow>
                  {
                    new Array(numDependentCols).fill(0).map(_ =>
                      <StyledTableCell style={{ border: "none" }} className={classes.tableDataCell} />
                    )
                  }
                  {
                    new Array(numTargetStates).fill(0).map((_, index) =>
                      <StyledTableCell style={{ border: "none" }} className={classes.tableDataCell}>
                        <Tooltip title="Delete this state">
                          <IconButton onClick={e => this.handleDeleteState(e, index)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </StyledTableCell>
                    )
                  }
                </StyledTableRow>
              }
              <StyledTableRow>
                {
                  parents.map((parent, index) => {
                    const parentLabelName = (parent.getValue() as BayesianNetworkVertexValue).getLabel().getName();
                    return (<StyledTableCell key={index} className={classes.tableDataCell}>{parentLabelName}</StyledTableCell>)
                  })
                }
                {
                  targetStateNames.map((targetStateName, index) =>
                    <StyledTableCell key={index} className={classes.tableDataCell}>
                      <TextField
                        value={targetStateName}
                        onChange={e => this.handleTargetStateNameChange(e, index)}
                        error={stateNameError}
                        helperText={stateNameError ? 'States cannot be empty or duplicate name' : null}
                      />
                    </StyledTableCell>
                  )
                }
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {
                transposedMatrix.map((row, rowIndex) =>
                  <StyledTableRow key={rowIndex}>
                    {
                      row.map((data, colIndex) =>
                        {
                          const cptError = !InputRules.isEqualToOne(row.slice(parents.length) as number[]);
                          return (
                            <StyledTableCell key={colIndex} className={classes.tableDataCell}>
                              {
                                numDependentCols <= colIndex ?
                                  <TextField
                                    value={data}
                                    type="number"
                                    onChange={e => this.handleProbabilities(e, rowIndex, colIndex)}
                                    inputProps={{
                                      step: 0.01, min: 0, max: 1, style: { fontSize: 13 },
                                    }}
                                    error={cptError}
                                    helperText={cptError ? 'The sum must equal to 1' : null}
                                  /> : data
                              }
                            </StyledTableCell>
                          );
                        }
                      )
                    }
                  </StyledTableRow>
                )
              }
            </TableBody>
          </Table>
        </div>

      </VertexSelectionDialog>
    );
  }
}

export default withStyles(styles)(BayesianNetworkMxGraphDialog);
